import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai';

const TTL_MS = 24 * 60 * 60 * 1000; // 1 day
const MAX_ENTRIES = 15;

type SwipedUsersCache = Map<string, number>;
type SwipedUsersAction =
  | { type: 'add'; userId: string }
  | { type: 'remove'; userId: string }
  | { type: 'clear' };

const swipedUsersCacheAtom = atom<SwipedUsersCache>(new Map());

function filterExpired(cache: SwipedUsersCache): SwipedUsersCache {
  const now = Date.now();
  return new Map(Iterator.from(cache).filter(([, ts]) => now - ts < TTL_MS));
}

function trimToMax(cache: SwipedUsersCache): SwipedUsersCache {
  if (cache.size <= MAX_ENTRIES) return cache;
  return new Map(Iterator.from(cache).drop(cache.size - MAX_ENTRIES));
}

export const swipedUsersAtom = atom(
  get => {
    const cache = get(swipedUsersCacheAtom);
    const now = Date.now();
    return new Set(
      Iterator.from(cache)
        .filter(([, ts]) => now - ts < TTL_MS)
        .map(([userId]) => userId),
    );
  },
  (get, set, action: SwipedUsersAction) => {
    if (action.type === 'clear') {
      set(swipedUsersCacheAtom, new Map());
      return;
    }

    const filtered = filterExpired(get(swipedUsersCacheAtom));

    if (action.type === 'add') {
      filtered.set(action.userId, Date.now());
      set(swipedUsersCacheAtom, trimToMax(filtered));
    } else {
      filtered.delete(action.userId);
      set(swipedUsersCacheAtom, filtered);
    }
  },
);

export function clearSwipedUsers(): void {
  const store = getDefaultStore();
  store.set(swipedUsersAtom, { type: 'clear' });
}

export function useSwipedUsers(): Set<string> {
  return useAtomValue(swipedUsersAtom);
}

export function useAddSwipedUser() {
  const setSwipedUsers = useSetAtom(swipedUsersAtom);
  return (userId: string) => {
    setSwipedUsers({ type: 'add', userId });
  };
}

export function useRemoveSwipedUser() {
  const setSwipedUsers = useSetAtom(swipedUsersAtom);
  return (userId: string) => {
    setSwipedUsers({ type: 'remove', userId });
  };
}

export function getSwipedUsersSync(): Set<string> {
  const store = getDefaultStore();
  return store.get(swipedUsersAtom);
}
